import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { getMarketRadar, getRentRadar, getDealScores } from "../api";
import { Radar, TrendingUp, Home, ArrowUpRight, ArrowDownRight, Zap } from "lucide-react";
import GrowthAutomation from "../components/growth/GrowthAutomation";
import GrowthIntelligence from "../components/growth/GrowthIntelligence";
import GrowthProspecting from "../components/growth/GrowthProspecting";

const fmt = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;

const Growth = () => {
  const { user, loading } = useAuth();
  const [market, setMarket] = useState([]);
  const [rent, setRent] = useState([]);
  const [deals, setDeals] = useState([]);
  const [gGroup, setGGroup] = useState("intel");
  const [gTab, setGTab] = useState("market");
  const isAdmin = user && (user.role === "admin" || user.role === "agent");

  useEffect(() => {
    if (isAdmin) {
      getMarketRadar().then(setMarket).catch(() => {});
      getRentRadar().then(setRent).catch(() => {});
      getDealScores().then(setDeals).catch(() => {});
    }
  }, [isAdmin]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#8a8a8a] font-display text-[20px]">Loading...</div>;
  if (!isAdmin) return <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center"><h1 className="font-display text-[30px] text-[#2b2b2b] mb-3">Team access only</h1><p className="text-[14px] text-[#8a8a8a]">Sign in with an agent or admin account.</p></div>;

  const scoreColor = (s) => s >= 80 ? "text-green-600" : s >= 60 ? "text-amber-600" : "text-[#8a8a8a]";
  const GROWTH_GROUPS = [
    { key: "intel", label: "Intelligence", tabs: [["market", "Market Radar"], ["rent", "Rent Radar"], ["intelligence", "Area Intelligence"]] },
    { key: "pipeline", label: "Pipeline", tabs: [["deals", "Deal Scores"], ["prospecting", "Prospecting"]] },
    { key: "ops", label: "Automation", tabs: [["automation", "Automation"]] },
  ];
  const curGGroup = GROWTH_GROUPS.find((g) => g.key === gGroup) || GROWTH_GROUPS[0];

  return (
    <div className="min-h-[70vh] bg-[#faf7f2]">
      <div className="bg-[#1c1c1e] py-14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-2 flex items-center gap-2"><Radar size={15} /> Growth & Ops</p>
          <h1 className="font-display text-white text-[34px] md:text-[44px] font-300">Market Intelligence</h1>
          <p className="text-white/50 text-[13px] mt-2">Competitor & rent signals and deal scoring, powered by live PropertyData & HM Land Registry feeds.</p>
        </div>
      </div>
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12">
        <Tabs value={gTab} onValueChange={setGTab}>
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 mb-3" data-testid="growth-group-nav">
              {GROWTH_GROUPS.map((g) => (
                <button key={g.key} onClick={() => { setGGroup(g.key); setGTab(g.tabs[0][0]); }} data-testid={`growth-group-${g.key}`}
                  className={`px-4 py-2 rounded-full text-[13px] font-500 transition-colors ${gGroup === g.key ? "bg-[#1c1c1e] text-white" : "bg-white border border-[#f0ece5] text-[#6a6a6a] hover:border-[#b8935f]"}`}>
                  {g.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 bg-white border border-[#f0ece5] rounded-xl p-1.5" data-testid="growth-subtabs">
              {curGGroup.tabs.map(([v, l]) => (
                <button key={v} onClick={() => setGTab(v)} data-testid={`growth-tab-${v}`}
                  className={`px-4 py-2 rounded-lg text-[13px] transition-colors ${gTab === v ? "bg-[#b8935f] text-white" : "text-[#6a6a6a] hover:bg-[#faf7f2]"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <TabsContent value="market">
            <div className="grid sm:grid-cols-3 gap-5">
              {market.map((m) => (
                <div key={m.id} className={`bg-white rounded-2xl border p-6 ${m.agent === "Anadear Estates" ? "border-[#b8935f]" : "border-[#f0ece5]"}`}>
                  <p className="text-[13px] text-[#a0a0a0]">{m.area}</p>
                  <p className="font-display text-[19px] font-500 text-[#2b2b2b] mb-3">{m.agent}</p>
                  <p className="font-display text-[32px] font-500 text-[#2b2b2b]">{m.liveStock}<span className="text-[13px] text-[#a0a0a0] font-400 ml-1">live</span></p>
                  <div className="flex items-center justify-between mt-3 text-[13px]">
                    <span className={`flex items-center gap-1 ${m.changeWeek >= 0 ? "text-green-600" : "text-red-500"}`}>{m.changeWeek >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}{Math.abs(m.changeWeek)} this week</span>
                    <span className="text-[#8a8a8a]">avg {fmt(m.avgPrice)}</span>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="rent">
            <div className="space-y-2">
              {rent.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-4 rounded-xl bg-white border border-[#f0ece5]">
                  <div><p className="text-[15px] font-500 text-[#2b2b2b]">{r.area}</p><p className="text-[13px] text-[#8a8a8a]">{r.alert}</p></div>
                  <div className="text-right"><p className="text-[15px] font-500 text-[#b8935f]">{fmt(r.rent)} pcm</p><span className={`text-[12px] ${r.delta >= 0 ? "text-green-600" : "text-red-500"}`}>{r.delta >= 0 ? "+" : ""}{r.delta}% vs avg</span></div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="deals">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {deals.map((d) => (
                <div key={d.id} className="bg-white rounded-2xl border border-[#f0ece5] overflow-hidden">
                  {d.image && <img src={d.image} alt="" className="w-full h-32 object-cover" />}
                  <div className="p-5">
                    <p className="font-display text-[16px] font-500 text-[#2b2b2b] mb-2">{d.title}</p>
                    <div className="flex items-center justify-between">
                      <div><p className={`font-display text-[28px] font-500 ${scoreColor(d.score)}`}>{d.score}</p><p className="text-[11px] text-[#a0a0a0] uppercase">deal score</p></div>
                      <div className="text-right"><p className="text-[15px] font-500 text-[#2b2b2b]">{d.yield}%</p><p className="text-[11px] text-[#a0a0a0]">est. yield</p></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="automation">
            <GrowthAutomation />
          </TabsContent>

          <TabsContent value="prospecting">
            <GrowthProspecting />
          </TabsContent>

          <TabsContent value="intelligence">
            <GrowthIntelligence />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Growth;
